import * as React from "react";
import { FILTER_SEPARATOR } from "@/constants";
import {
	Alert,
	Box,
	Button,
	Card,
	Chip,
	Grid,
	Link,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { CaretRightIcon } from "@phosphor-icons/react/dist/ssr/CaretRight";
import { useNavigate, useParams } from "react-router-dom";

import type { ProgrammeMemberStats } from "@/hooks/api/use-campaign-programmes";
import {
	useCampaignProgramme,
	useCampaignProgrammeCampaigns,
	useCampaignProgrammeDays,
	useCampaignProgrammeRollup,
} from "@/hooks/api/use-campaign-programmes";
import type { CampaignRequestMarket } from "@/hooks/api/use-campaign-requests";
import { useCampaignRequest } from "@/hooks/api/use-campaign-requests";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { RouterLink } from "@/components/core/link";

import { HelpTooltip, MeteringGap, NOT_METERED, NOT_METERED_HELP, StateChip } from "../components";
import { marketClock, marketFlag, marketLabel } from "../constants";
import { daySpan, formatCount, formatDay, formatRate, formatTalkTime, isLikelyRedial, todayIn } from "./progress";

/**
 * The programme rollup — UX §13, "four markets, and the day drill-down".
 *
 * Three levels, and the whole point is that they are free: campaign → market → day → the calls
 * themselves. None of it is extra analysis; it falls out of one campaign per market, which is
 * also why it answers for campaigns that ran long before any of this existed.
 *
 * The market comparison is the part that sells. Bahasa at 42% against English at 35% is a number
 * a client repeats internally, so a market ahead of the programme's own rate is marked rather
 * than left to be spotted.
 *
 * Reporting timezone is stated in the header because four markets have four day boundaries: the
 * rollup picks one and says which, while each market's drill-down is cut in its own local time.
 */

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }): React.JSX.Element {
	const tile = (
		<Card variant="outlined" sx={{ p: 2, height: "100%" }}>
			<Typography variant="caption" color="text.secondary" sx={{ letterSpacing: ".06em", textTransform: "uppercase" }}>
				{label}
			</Typography>
			<Typography variant="h6" sx={{ mt: 0.5 }}>
				{value}
			</Typography>
		</Card>
	);

	return hint ? (
		<HelpTooltip title={hint}>
			<Box tabIndex={0} sx={{ cursor: "help", height: "100%" }}>
				{tile}
			</Box>
		</HelpTooltip>
	) : (
		tile
	);
}

export function CampaignRequestLive(): React.JSX.Element {
	const { requestId } = useParams<{ requestId: string }>();
	const navigate = useNavigate();

	const { data: request, isLoading: isRequestLoading } = useCampaignRequest(requestId);
	const programmeId = request?.campaignProgrammeId ?? null;

	const { data: programme } = useCampaignProgramme(programmeId);
	const { data: members = [] } = useCampaignProgrammeCampaigns(programmeId);
	// While it is dialing the figures have to move on their own; a client should never reload to
	// find out how a campaign is going.
	const {
		data: rollup,
		isLoading: isRollupLoading,
		isError: rollupFailed,
	} = useCampaignProgrammeRollup(programmeId, {
		refetchMs: request?.state === "running" ? 60_000 : undefined,
	});

	const [selectedCampaignId, setSelectedCampaignId] = React.useState<string | null>(null);
	const {
		data: days = [],
		isLoading: isDaysLoading,
		isError: daysFailed,
	} = useCampaignProgrammeDays(programmeId, selectedCampaignId);

	// The rollup names each market by campaign id; the request's own market rows are what turn that
	// back into a country, and they carry the dates the programme was scheduled for.
	const marketByCampaign = React.useMemo(() => {
		const map = new Map<string, CampaignRequestMarket>();
		for (const market of request?.marketSchedules ?? []) {
			if (market.campaignId) map.set(market.campaignId, market);
		}
		return map;
	}, [request]);

	const agentByCampaign = React.useMemo(() => {
		const map = new Map<string, string>();
		for (const member of members) {
			if (member.agent) map.set(member.id, member.agent.name);
		}
		return map;
	}, [members]);

	const marketName = React.useCallback(
		(member: ProgrammeMemberStats): string => {
			const market = marketByCampaign.get(member.campaignId);
			return market ? `${marketFlag(market.market)} ${marketLabel(market.market)}`.trim() : member.title;
		},
		[marketByCampaign]
	);

	const selected = rollup?.members.find((member) => member.campaignId === selectedCampaignId) ?? null;

	if (isRequestLoading) {
		return (
			<Box sx={{ m: "var(--Content-margin)", p: "var(--Content-padding)", width: "var(--Content-width)" }}>
				<Stack spacing={2}>
					<Skeleton height={48} variant="rounded" />
					<Skeleton height={120} variant="rounded" />
					<Skeleton height={280} variant="rounded" />
				</Stack>
			</Box>
		);
	}

	if (!request) {
		return (
			<Box sx={{ m: "var(--Content-margin)", p: "var(--Content-padding)", width: "var(--Content-width)" }}>
				<Alert severity="error">This request could not be loaded.</Alert>
			</Box>
		);
	}

	if (!programmeId) {
		return (
			<Box sx={{ m: "var(--Content-margin)", p: "var(--Content-padding)", width: "var(--Content-width)" }}>
				<Stack spacing={2} sx={{ alignItems: "flex-start" }}>
					<Alert severity="info">
						Nothing has been dialled for this request yet. Results appear here as soon as the campaigns are built and
						the first calls go out.
					</Alert>
					<Button startIcon={<ArrowLeftIcon />} onClick={() => navigate(`/campaign-requests/${request.id}`)}>
						Back to the Request
					</Button>
				</Stack>
			</Box>
		);
	}

	const total = rollup?.total;
	const starts = request.marketSchedules.map((market) => market.dialStartDate).filter(Boolean) as string[];
	const ends = request.marketSchedules.map((market) => market.dialEndDate).filter(Boolean) as string[];
	// The zone the API says it cut the rollup in, not the one this market's country usually keeps:
	// the client picks a zone per market in the wizard, and `marketClock` only knows a country's
	// ordinary one — "AEST" over a programme reported in Australia/Perth is three hours of lie.
	const leadMarket = request.marketSchedules.find((market) => market.position === 0);
	const reportingClock = programme?.reportingTimezone || marketClock(leadMarket?.market) || "UTC";

	const span = daySpan(starts.toSorted()[0] ?? null, ends.toSorted().at(-1) ?? null);
	const elapsed =
		span === null
			? null
			: Math.min(span, Math.max(1, daySpan(starts.toSorted()[0] ?? null, todayIn(programme?.reportingTimezone)) ?? 1));

	const headline = [
		elapsed && span ? `Day ${elapsed} of ${span}` : null,
		`reporting in ${reportingClock}`,
		// The scheduled start, and said as such: no call has necessarily been placed on it.
		starts.length > 0 ? `first calls scheduled ${formatDay(starts.toSorted()[0])}` : null,
	]
		.filter(Boolean)
		.join(" · ");

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Stack spacing={3}>
					<Breadcrumbs>
						<Link
							color="text.primary"
							component={RouterLink}
							href={`/campaign-requests/${request.id}`}
							sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
							variant="subtitle2"
						>
							<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
							{request.title || "Campaign request"}
						</Link>
						<Typography variant="subtitle2" color="text.secondary" whiteSpace="pre">
							/
						</Typography>
						<Typography variant="subtitle2" color="text.secondary" whiteSpace="pre">
							Results
						</Typography>
					</Breadcrumbs>

					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={2}
						sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
					>
						<Box>
							<Typography variant="h4">{programme?.title || request.title}</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								{headline}
							</Typography>
						</Box>

						<StateChip state={request.state} variant="client" size="medium" />
					</Stack>
				</Stack>

				{/*
				 * UX §13's header carries "cost to date" beside called, reached and reach rate. Five of
				 * these six tiles are counted off calls that actually happened; the sixth cannot be, so
				 * it says so. Talk time next to it is the honest half of the same question — the seconds
				 * are real and recorded, what a second cost is not written down anywhere. See
				 * `MeteringGap` for what would have to exist.
				 */}
				<Grid container spacing={2}>
					<Grid size={{ xs: 6, sm: 4, md: 2 }}>
						<Kpi label="Called" value={total ? formatCount(total.dialed) : "—"} />
					</Grid>
					<Grid size={{ xs: 6, sm: 4, md: 2 }}>
						<Kpi label="Reached" value={total ? formatCount(total.reached) : "—"} />
					</Grid>
					<Grid size={{ xs: 6, sm: 4, md: 2 }}>
						<Kpi label="Reach rate" value={total ? formatRate(total.reachRate) : "—"} />
					</Grid>
					<Grid size={{ xs: 6, sm: 4, md: 2 }}>
						<Kpi
							label="Talk time"
							value={total ? formatTalkTime(total.talkSeconds) : "—"}
							hint="Time actually connected, added up across every call. Call time is billed against this."
						/>
					</Grid>
					<Grid size={{ xs: 6, sm: 4, md: 2 }}>
						<Kpi label="Booked" value={total ? formatCount(total.booked) : "—"} />
					</Grid>
					<Grid size={{ xs: 6, sm: 4, md: 2 }}>
						<Kpi label="Cost to date" value={NOT_METERED} hint={NOT_METERED_HELP} />
					</Grid>
				</Grid>

				<MeteringGap variant="client" />

				<Card variant="outlined" sx={{ overflowX: "auto" }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 600 }}>Market</TableCell>
								<TableCell sx={{ fontWeight: 600 }}>Agent</TableCell>
								<TableCell sx={{ fontWeight: 600 }} align="right">
									Called
								</TableCell>
								<TableCell sx={{ fontWeight: 600 }} align="right">
									Reached
								</TableCell>
								<TableCell sx={{ fontWeight: 600 }} align="right">
									Reach
								</TableCell>
								<TableCell sx={{ fontWeight: 600 }} align="right">
									Talk Time
								</TableCell>
								<TableCell sx={{ fontWeight: 600 }} align="right">
									Booked
								</TableCell>
								<TableCell aria-label="Details" />
							</TableRow>
						</TableHead>

						<TableBody>
							{isRollupLoading ? (
								<TableRow>
									<TableCell colSpan={8}>
										<Skeleton height={24} variant="rounded" />
									</TableCell>
								</TableRow>
							) : null}

							{rollupFailed ? (
								<TableRow>
									<TableCell colSpan={8}>
										<Typography variant="body2" color="error.main" sx={{ textAlign: "center" }}>
											These results could not be loaded just now. Reload to try again — nothing about the campaign has
											changed.
										</Typography>
									</TableCell>
								</TableRow>
							) : null}

							{!rollupFailed && !isRollupLoading && (rollup?.members.length ?? 0) === 0 ? (
								<TableRow>
									<TableCell colSpan={8}>
										<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
											No calls have been placed yet.
										</Typography>
									</TableCell>
								</TableRow>
							) : null}

							{rollup?.members.map((member) => {
								const ahead = Boolean(total && member.reachRate > total.reachRate);

								return (
									<TableRow key={member.campaignId} selected={member.campaignId === selectedCampaignId}>
										<TableCell>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{marketName(member)}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{member.timezone}
											</Typography>
										</TableCell>
										<TableCell>{agentByCampaign.get(member.campaignId) ?? "—"}</TableCell>
										<TableCell align="right">{formatCount(member.dialed)}</TableCell>
										<TableCell align="right">{formatCount(member.reached)}</TableCell>
										<TableCell align="right">
											<HelpTooltip
												title={
													ahead
														? "Ahead of this campaign's overall reach rate"
														: "At or below this campaign's overall reach rate"
												}
											>
												<Typography
													tabIndex={0}
													variant="body2"
													sx={{
														color: ahead ? "success.main" : "text.primary",
														cursor: "help",
														fontWeight: ahead ? 700 : 400,
													}}
												>
													{formatRate(member.reachRate)}
												</Typography>
											</HelpTooltip>
										</TableCell>
										<TableCell align="right">{formatTalkTime(member.talkSeconds)}</TableCell>
										<TableCell align="right">
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{formatCount(member.booked)}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Button
												size="small"
												endIcon={<CaretRightIcon />}
												onClick={() =>
													setSelectedCampaignId((current) => (current === member.campaignId ? null : member.campaignId))
												}
											>
												{member.campaignId === selectedCampaignId ? "Hide days" : "View days"}
											</Button>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</Card>

				{selected ? (
					<Card variant="outlined" sx={{ p: 2 }}>
						<Stack spacing={2}>
							<Box>
								<Typography variant="h6">{marketName(selected)} — day by day</Typography>
								<Typography variant="caption" color="text.secondary">
									Each day runs on {selected.timezone}, this market&apos;s own clock
								</Typography>
							</Box>

							{isDaysLoading ? <Skeleton height={80} variant="rounded" /> : null}

							{daysFailed ? (
								<Typography variant="body2" color="error.main">
									This market&rsquo;s days could not be loaded just now. Reload to try again.
								</Typography>
							) : null}

							{!daysFailed && !isDaysLoading && days.length === 0 ? (
								<Typography variant="body2" color="text.secondary">
									Nothing has been dialled in this market yet.
								</Typography>
							) : null}

							{days.length > 0 ? (
								<Box sx={{ overflowX: "auto" }}>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontWeight: 600 }}>Day</TableCell>
												<TableCell sx={{ fontWeight: 600 }} align="right">
													Called
												</TableCell>
												<TableCell sx={{ fontWeight: 600 }} align="right">
													Reached
												</TableCell>
												<TableCell sx={{ fontWeight: 600 }} align="right">
													Reach
												</TableCell>
												<TableCell sx={{ fontWeight: 600 }} align="right">
													Talk Time
												</TableCell>
												<TableCell sx={{ fontWeight: 600 }} align="right">
													Booked
												</TableCell>
												<TableCell aria-label="Call log" />
											</TableRow>
										</TableHead>

										<TableBody>
											{days.map((day, index) => (
												<TableRow key={day.day}>
													<TableCell>
														<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
															<Typography variant="body2" sx={{ fontWeight: 600 }}>
																{formatDay(day.day)}
															</Typography>
															{isLikelyRedial(days, index) ? (
																<HelpTooltip title="A return to the same leads. It counts against the attempt cap agreed for this market.">
																	<Chip tabIndex={0} label="Second attempt" size="small" variant="outlined" />
																</HelpTooltip>
															) : null}
														</Stack>
													</TableCell>
													<TableCell align="right">{formatCount(day.dialed)}</TableCell>
													<TableCell align="right">{formatCount(day.reached)}</TableCell>
													<TableCell align="right">{formatRate(day.reachRate)}</TableCell>
													<TableCell align="right">{formatTalkTime(day.talkSeconds)}</TableCell>
													<TableCell align="right">{formatCount(day.booked)}</TableCell>
													<TableCell align="right">
														<HelpTooltip title="Opens the call log for this market. The log filters by campaign, not by day.">
															<Button
																size="small"
																endIcon={<CaretRightIcon />}
																onClick={() =>
																	navigate(
																		`/call-logs?campaignId=${encodeURIComponent(`${selected.title}${FILTER_SEPARATOR}${selected.campaignId}`)}`
																	)
																}
															>
																{formatCount(day.dialed)} calls
															</Button>
														</HelpTooltip>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</Box>
							) : null}
						</Stack>
					</Card>
				) : null}
			</Stack>
		</Box>
	);
}
